import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ExportPublishPanel from '../components/ExportPublishPanel';
import AppShell from '../components/AppShell';
import { 
  Sparkles, Loader2, Download, Video, Music, Image as ImageIcon,
  Package, Check, AlertCircle, Play, Pause, ChevronRight,
  Layers, Wand2, RefreshCw, ArrowRight, Zap, Clock, CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CombineStudioPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('create');

  // Creation state
  const [isCreating, setIsCreating] = useState(false);
  const [prompt, setPrompt] = useState('');
  
  // What to create
  const [createOptions, setCreateOptions] = useState({
    image: true,
    video: true,
    music: true
  });

  // Settings
  const [settings, setSettings] = useState({
    platform: 'instagram',
    category: 'general',
    voice: 'nova',
    imageStyle: 'photorealistic',
    musicGenre: 'pop',
    musicMood: 'upbeat'
  });

  // Progress tracking
  const [progress, setProgress] = useState({
    image: { status: 'pending', data: null, error: null },
    video: { status: 'pending', data: null, error: null },
    music: { status: 'pending', data: null, error: null }
  });

  // Generated content packages
  const [packages, setPackages] = useState([]);

  // Platforms and categories
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchPlatforms();
    fetchPackages();
  }, [token]);

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      setPlatforms(response.data.platforms || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch platforms');
    }
  };

  const fetchPackages = async () => {
    // Load from localStorage for now (could be a backend endpoint)
    const saved = localStorage.getItem('delphi_content_packages');
    if (saved) {
      try {
        setPackages(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved packages');
      }
    }
  };

  const savePackage = (pkg) => {
    const updated = [pkg, ...packages].slice(0, 20); // Keep last 20
    setPackages(updated);
    localStorage.setItem('delphi_content_packages', JSON.stringify(updated));
  };

  const handleCreate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe what you want to create');
      return;
    }

    const selected = Object.entries(createOptions).filter(([_, v]) => v).map(([k]) => k);
    if (selected.length === 0) {
      toast.error('Please select at least one content type to create');
      return;
    }

    setIsCreating(true);
    setActiveTab('progress');

    // Reset progress
    const initialProgress = {
      image: { status: createOptions.image ? 'pending' : 'skipped', data: null, error: null },
      video: { status: createOptions.video ? 'pending' : 'skipped', data: null, error: null },
      music: { status: createOptions.music ? 'pending' : 'skipped', data: null, error: null }
    };
    setProgress(initialProgress);

    const results = { ...initialProgress };
    const packageData = {
      id: Date.now().toString(),
      prompt,
      created_at: new Date().toISOString(),
      items: {}
    };

    // Create Image
    if (createOptions.image) {
      setProgress(prev => ({ ...prev, image: { ...prev.image, status: 'creating' } }));
      try {
        const response = await axios.post(`${API}/images/generate`, {
          prompt: prompt,
          style: settings.imageStyle
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        results.image = { status: 'complete', data: response.data, error: null };
        packageData.items.image = response.data;
        setProgress(prev => ({ ...prev, image: results.image }));
        toast.success('Image generated!');
      } catch (error) {
        results.image = { status: 'error', data: null, error: error.response?.data?.detail || 'Failed to generate image' };
        setProgress(prev => ({ ...prev, image: results.image }));
      }
    }

    // Create Video - video generation never blocks on music
    if (createOptions.video) {
      setProgress(prev => ({ ...prev, video: { ...prev.video, status: 'creating' } }));
      try {
        const response = await axios.post(`${API}/reels/`, {
          prompt: prompt,
          platform: settings.platform,
          category: settings.category,
          voice: settings.voice,
          include_music: createOptions.music  // Tell backend if we want music
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        results.video = { status: 'processing', data: response.data, error: null };
        packageData.items.video = response.data;
        setProgress(prev => ({ ...prev, video: results.video }));
        toast.success('Video creation started!');

        // Poll for video status
        pollVideoStatus(response.data.id, results, packageData);
      } catch (error) {
        results.video = { status: 'error', data: null, error: error.response?.data?.detail || 'Failed to create video' };
        setProgress(prev => ({ ...prev, video: results.video }));
      }
    }

    // Create Standalone Music (only if video not selected, since video includes music)
    if (createOptions.music && !createOptions.video) {
      setProgress(prev => ({ ...prev, music: { ...prev.music, status: 'creating' } }));
      
      try {
        const response = await axios.post(`${API}/music/generate`, {
          prompt: prompt,
          duration: 30,
          genre: settings.musicGenre,
          mood: settings.musicMood,
          use_enhanced: false  // Don't request enhanced for combined package
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        results.music = { status: 'processing', data: response.data, error: null };
        packageData.items.music = response.data;
        setProgress(prev => ({ ...prev, music: results.music }));
        toast.success('Music generation started!');
        
        // Poll for music completion
        pollMusicStatus(response.data.id, results, packageData);
      } catch (error) {
        results.music = { status: 'error', data: null, error: error.response?.data?.detail || 'Music generation unavailable' };
        setProgress(prev => ({ ...prev, music: results.music }));
        // Don't show error toast - just mark as unavailable
      }
    } else if (createOptions.music && createOptions.video) {
      // Music is included in video, mark as processing (will be ready when video is ready)
      results.music = { status: 'included', data: { note: 'Music included in video' }, error: null };
      setProgress(prev => ({ ...prev, music: results.music }));
    }

    // Save package if at least one item succeeded
    if (Object.values(packageData.items).length > 0) {
      savePackage(packageData);
    }

    setIsCreating(false);
  };

  const pollVideoStatus = async (videoId, results, packageData) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setProgress(prev => ({ 
          ...prev, 
          video: { ...prev.video, status: 'timeout', error: 'Video rendering timed out' } 
        }));
        return;
      }

      try {
        const response = await axios.get(`${API}/reels/${videoId}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const videoData = { ...results.video.data, ...response.data };
        
        if (response.data.status === 'ready') {
          setProgress(prev => ({ 
            ...prev, 
            video: { status: 'complete', data: videoData, error: null } 
          }));
          packageData.items.video = videoData;
          savePackage(packageData);
          toast.success('Video rendering complete!');
          return;
        } else if (response.data.status === 'failed') {
          setProgress(prev => ({ 
            ...prev, 
            video: { status: 'error', data: videoData, error: 'Video rendering failed' } 
          }));
          return;
        }

        setProgress(prev => ({ 
          ...prev, 
          video: { 
            status: 'processing', 
            data: videoData, 
            error: null,
            progress: response.data.render_progress,
            message: response.data.render_message
          } 
        }));

        attempts++;
        setTimeout(poll, 3000);
      } catch (error) {
        console.error('Status poll error');
        attempts++;
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const pollMusicStatus = async (musicId, results, packageData) => {
    const maxAttempts = 30;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setProgress(prev => ({ 
          ...prev, 
          music: { ...prev.music, status: 'complete', data: prev.music.data } 
        }));
        return;
      }

      try {
        const response = await axios.get(`${API}/music/${musicId}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.status === 'ready') {
          setProgress(prev => ({ 
            ...prev, 
            music: { status: 'complete', data: response.data, error: null } 
          }));
          packageData.items.music = response.data;
          savePackage(packageData);
          
          // Show toast only if there was a fallback message
          if (response.data.toast_message) {
            toast.info(response.data.toast_message);
          } else {
            toast.success('Music ready!');
          }
          return;
        } else if (response.data.status === 'failed') {
          setProgress(prev => ({ 
            ...prev, 
            music: { status: 'error', data: response.data, error: 'Music generation failed' } 
          }));
          return;
        }

        attempts++;
        setTimeout(poll, 2000);
      } catch (error) {
        attempts++;
        setTimeout(poll, 3000);
      }
    };

    poll();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete': return <CheckCircle2 className="w-5 h-5 text-green-400" />;
      case 'included': return <CheckCircle2 className="w-5 h-5 text-blue-400" />;
      case 'creating':
      case 'processing': return <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'skipped': return <div className="w-5 h-5 rounded-full bg-gray-500/30" />;
      default: return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'complete': return 'Complete';
      case 'creating': return 'Creating...';
      case 'processing': return 'Processing...';
      case 'included': return 'Included in video';
      case 'error': return 'Failed';
      case 'skipped': return 'Skipped';
      case 'timeout': return 'Timed out';
      default: return 'Pending';
    }
  };

  const voiceOptions = [
    { value: 'nova', label: 'Nova', desc: 'Energetic' },
    { value: 'alloy', label: 'Alloy', desc: 'Balanced' },
    { value: 'echo', label: 'Echo', desc: 'Calm' },
    { value: 'fable', label: 'Fable', desc: 'Storytelling' },
    { value: 'onyx', label: 'Onyx', desc: 'Authoritative' },
    { value: 'shimmer', label: 'Shimmer', desc: 'Cheerful' }
  ];

  const imageStyles = [
    { value: 'photorealistic', label: 'Photorealistic' },
    { value: 'digital-art', label: 'Digital Art' },
    { value: 'anime', label: 'Anime' },
    { value: 'artistic', label: 'Artistic' }
  ];

  const musicGenres = [
    { value: 'pop', label: 'Pop' },
    { value: 'electronic', label: 'Electronic' },
    { value: 'hiphop', label: 'Hip Hop' },
    { value: 'ambient', label: 'Ambient' },
    { value: 'cinematic', label: 'Cinematic' },
    { value: 'lofi', label: 'Lo-Fi' }
  ];

  const musicMoods = [
    { value: 'upbeat', label: 'Upbeat', emoji: '🎉' },
    { value: 'energetic', label: 'Energetic', emoji: '⚡' },
    { value: 'calm', label: 'Calm', emoji: '🌊' },
    { value: 'inspiring', label: 'Inspiring', emoji: '✨' },
    { value: 'dramatic', label: 'Dramatic', emoji: '🎭' },
    { value: 'playful', label: 'Playful', emoji: '🎈' }
  ];

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10 max-w-6xl mx-auto">
          {/* Studio Tabs Navigation */}
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
            <Link to="/studio/combine" className="px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-primary to-purple-500 text-white font-medium whitespace-nowrap text-sm sm:text-base">
              <Package className="w-4 h-4 inline mr-1 sm:mr-2" />Combine
            </Link>
            <Link to="/studio/images" className="px-3 sm:px-4 py-2 rounded-full bg-secondary text-muted-foreground hover:text-foreground font-medium whitespace-nowrap text-sm sm:text-base">
              <ImageIcon className="w-4 h-4 inline mr-1 sm:mr-2" />Images
            </Link>
            <Link to="/studio/videos" className="px-3 sm:px-4 py-2 rounded-full bg-secondary text-muted-foreground hover:text-foreground font-medium whitespace-nowrap text-sm sm:text-base">
              <Video className="w-4 h-4 inline mr-1 sm:mr-2" />Videos
            </Link>
            <Link to="/studio/music" className="px-3 sm:px-4 py-2 rounded-full bg-secondary text-muted-foreground hover:text-foreground font-medium whitespace-nowrap text-sm sm:text-base">
              <Music className="w-4 h-4 inline mr-1 sm:mr-2" />Music
            </Link>
          </div>

          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Combine Engine</p>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2 tracking-tight text-white">
              Mix & Combine
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Create a complete content package - Image + Video + Music - from one prompt
            </p>
          </div>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-3 w-full max-w-md bg-secondary/50">
              <TabsTrigger value="create" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm">
                <Sparkles className="w-4 h-4 mr-1 sm:mr-2" />Create
              </TabsTrigger>
              <TabsTrigger value="progress" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm">
                <Zap className="w-4 h-4 mr-1 sm:mr-2" />Progress
              </TabsTrigger>
              <TabsTrigger value="packages" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm">
                <Layers className="w-4 h-4 mr-1 sm:mr-2" />Packages
              </TabsTrigger>
            </TabsList>

            {/* Create Tab */}
            <TabsContent value="create" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Panel - Main Settings */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="card-oracle p-4 sm:p-6 space-y-6">
                    {/* Prompt */}
                    <div className="space-y-2">
                      <Label className="text-lg font-semibold">Describe your content *</Label>
                      <Textarea
                        data-testid="combine-prompt-input"
                        placeholder="A motivational video about morning routines with energetic music and eye-catching thumbnail..."
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        className="input-oracle min-h-[120px] text-base"
                      />
                      <p className="text-xs text-muted-foreground">
                        One prompt to create matching content across all selected types
                      </p>
                    </div>

                    {/* What to Create */}
                    <div className="space-y-3">
                      <Label className="text-lg font-semibold">What to create</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Image Option */}
                        <button
                          onClick={() => setCreateOptions(prev => ({ ...prev, image: !prev.image }))}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            createOptions.image 
                              ? 'border-blue-500 bg-blue-500/10' 
                              : 'border-border hover:border-blue-500/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                              <ImageIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              createOptions.image ? 'border-blue-500 bg-blue-500' : 'border-muted-foreground'
                            }`}>
                              {createOptions.image && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </div>
                          <p className="font-semibold text-left">Image</p>
                          <p className="text-xs text-muted-foreground text-left">Thumbnail / Cover</p>
                        </button>

                        {/* Video Option */}
                        <button
                          onClick={() => setCreateOptions(prev => ({ ...prev, video: !prev.video }))}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            createOptions.video 
                              ? 'border-purple-500 bg-purple-500/10' 
                              : 'border-border hover:border-purple-500/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                              <Video className="w-5 h-5 text-white" />
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              createOptions.video ? 'border-purple-500 bg-purple-500' : 'border-muted-foreground'
                            }`}>
                              {createOptions.video && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </div>
                          <p className="font-semibold text-left">Video</p>
                          <p className="text-xs text-muted-foreground text-left">Reel / Short</p>
                        </button>

                        {/* Music Option */}
                        <button
                          onClick={() => setCreateOptions(prev => ({ ...prev, music: !prev.music }))}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            createOptions.music 
                              ? 'border-indigo-500 bg-indigo-500/10' 
                              : 'border-border hover:border-indigo-500/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                              <Music className="w-5 h-5 text-white" />
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              createOptions.music ? 'border-indigo-500 bg-indigo-500' : 'border-muted-foreground'
                            }`}>
                              {createOptions.music && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </div>
                          <p className="font-semibold text-left">Music</p>
                          <p className="text-xs text-muted-foreground text-left">Background Track</p>
                        </button>
                      </div>
                    </div>

                    {/* Create Button */}
                    <Button
                      onClick={handleCreate}
                      disabled={isCreating || !prompt.trim() || !Object.values(createOptions).some(v => v)}
                      className="w-full btn-oracle h-14 text-lg"
                      data-testid="combine-create-btn"
                    >
                      {isCreating ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Creating Package...
                        </>
                      ) : (
                        <>
                          <Package className="w-5 h-5 mr-2" />
                          Create Content Package
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Right Panel - Settings */}
                <div className="space-y-6">
                  {/* Image Settings */}
                  {createOptions.image && (
                    <div className="card-oracle p-4 space-y-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-blue-400" />
                        Image Settings
                      </h3>
                      <div className="space-y-2">
                        <Label className="text-sm">Style</Label>
                        <Select value={settings.imageStyle} onValueChange={(v) => setSettings({ ...settings, imageStyle: v })}>
                          <SelectTrigger className="input-oracle">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            {imageStyles.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Video Settings */}
                  {createOptions.video && (
                    <div className="card-oracle p-4 space-y-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Video className="w-4 h-4 text-purple-400" />
                        Video Settings
                      </h3>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-sm">Platform</Label>
                          <Select value={settings.platform} onValueChange={(v) => setSettings({ ...settings, platform: v })}>
                            <SelectTrigger className="input-oracle">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {platforms.map(p => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Voice</Label>
                          <Select value={settings.voice} onValueChange={(v) => setSettings({ ...settings, voice: v })}>
                            <SelectTrigger className="input-oracle">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {voiceOptions.map(v => (
                                <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Music Settings */}
                  {createOptions.music && (
                    <div className="card-oracle p-4 space-y-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Music className="w-4 h-4 text-indigo-400" />
                        Music Settings
                      </h3>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-sm">Genre</Label>
                          <Select value={settings.musicGenre} onValueChange={(v) => setSettings({ ...settings, musicGenre: v })}>
                            <SelectTrigger className="input-oracle">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {musicGenres.map(g => (
                                <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Mood</Label>
                          <div className="grid grid-cols-3 gap-2">
                            {musicMoods.map(m => (
                              <button
                                key={m.value}
                                onClick={() => setSettings({ ...settings, musicMood: m.value })}
                                className={`p-2 rounded-lg border text-center transition-all text-xs ${
                                  settings.musicMood === m.value
                                    ? 'border-indigo-500 bg-indigo-500/10'
                                    : 'border-border hover:border-indigo-500/50'
                                }`}
                              >
                                <span className="text-base">{m.emoji}</span>
                                <p className="mt-1">{m.label}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Progress Tab */}
            <TabsContent value="progress" className="space-y-6">
              <div className="max-w-2xl mx-auto space-y-4">
                {/* Image Progress */}
                <div className={`card-oracle p-4 ${progress.image.status === 'skipped' ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">Image</p>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(progress.image.status)}
                          <span className="text-sm text-muted-foreground">{getStatusText(progress.image.status)}</span>
                        </div>
                      </div>
                      {progress.image.error && (
                        <p className="text-sm text-red-400 mt-1">{progress.image.error}</p>
                      )}
                    </div>
                  </div>
                  {progress.image.data?.image_url && (
                    <div className="mt-4">
                      <img 
                        src={progress.image.data.image_url} 
                        alt="Generated" 
                        className="w-full max-h-48 object-contain rounded-lg"
                      />
                    </div>
                  )}
                </div>

                {/* Video Progress */}
                <div className={`card-oracle p-4 ${progress.video.status === 'skipped' ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Video className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">Video</p>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(progress.video.status)}
                          <span className="text-sm text-muted-foreground">{getStatusText(progress.video.status)}</span>
                        </div>
                      </div>
                      {progress.video.message && (
                        <p className="text-sm text-muted-foreground mt-1">{progress.video.message}</p>
                      )}
                      {progress.video.progress > 0 && progress.video.status === 'processing' && (
                        <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-purple-500 transition-all duration-500"
                            style={{ width: `${progress.video.progress}%` }}
                          />
                        </div>
                      )}
                      {progress.video.error && (
                        <p className="text-sm text-red-400 mt-1">{progress.video.error}</p>
                      )}
                    </div>
                  </div>
                  {progress.video.data?.video_url && progress.video.status === 'complete' && (
                    <div className="mt-4 space-y-3">
                      <video 
                        src={`${BACKEND_URL}${progress.video.data.video_url}`}
                        controls
                        className="w-full max-h-64 rounded-lg"
                      />
                      <ExportPublishPanel
                        title={prompt}
                        downloadUrl={`${BACKEND_URL}${progress.video.data.video_url}/download`}
                        downloadLabel="Download Video"
                      />
                    </div>
                  )}
                </div>

                {/* Music Progress */}
                <div className={`card-oracle p-4 ${progress.music.status === 'skipped' ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                      <Music className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">Music</p>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(progress.music.status)}
                          <span className="text-sm text-muted-foreground">{getStatusText(progress.music.status)}</span>
                        </div>
                      </div>
                      {progress.music.data?.note && (
                        <p className="text-sm text-primary mt-1">{progress.music.data.note}</p>
                      )}
                      {progress.music.error && (
                        <p className="text-sm text-red-400 mt-1">{progress.music.error}</p>
                      )}
                    </div>
                  </div>
                  {progress.music.data && progress.music.status === 'complete' && (
                    <div className="mt-4 p-3 bg-secondary/30 rounded-lg">
                      <p className="text-sm">
                        <span className="text-muted-foreground">Genre:</span> {progress.music.data.genre} • 
                        <span className="text-muted-foreground ml-2">Mood:</span> {progress.music.data.mood} • 
                        <span className="text-muted-foreground ml-2">Duration:</span> {progress.music.data.duration}s
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!isCreating && Object.values(progress).some(p => p.status === 'complete') && (
                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" className="flex-1" onClick={() => setActiveTab('create')}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Create Another
                    </Button>
                    <Button className="flex-1 btn-oracle" onClick={() => setActiveTab('packages')}>
                      <Layers className="w-4 h-4 mr-2" />
                      View Packages
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Packages Tab */}
            <TabsContent value="packages" className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Content Packages</h2>
                <Button variant="outline" onClick={() => setActiveTab('create')}>
                  <Package className="w-4 h-4 mr-2" />New Package
                </Button>
              </div>

              {packages.length === 0 ? (
                <div className="card-oracle p-12 text-center">
                  <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-lg font-medium mb-2">No packages yet</p>
                  <p className="text-muted-foreground mb-4">Create your first content package to see it here</p>
                  <Button onClick={() => setActiveTab('create')} className="btn-oracle">
                    <Sparkles className="w-4 h-4 mr-2" />Create Package
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {packages.map((pkg) => (
                    <div key={pkg.id} className="card-oracle p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{pkg.prompt}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(pkg.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          {pkg.items.image && <ImageIcon className="w-4 h-4 text-blue-400" />}
                          {pkg.items.video && <Video className="w-4 h-4 text-purple-400" />}
                          {pkg.items.music && <Music className="w-4 h-4 text-indigo-400" />}
                        </div>
                      </div>
                      
                      {/* Preview */}
                      <div className="grid grid-cols-3 gap-2">
                        {pkg.items.image?.image_url && (
                          <img 
                            src={pkg.items.image.image_url} 
                            alt="Package image" 
                            className="aspect-square object-cover rounded-lg"
                          />
                        )}
                        {pkg.items.video?.video_url && (
                          <div className="aspect-square bg-purple-500/20 rounded-lg flex items-center justify-center">
                            <Video className="w-6 h-6 text-purple-400" />
                          </div>
                        )}
                        {pkg.items.music && (
                          <div className="aspect-square bg-indigo-500/20 rounded-lg flex items-center justify-center">
                            <Music className="w-6 h-6 text-indigo-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
      </div>
    </AppShell>
  );
};

export default CombineStudioPage;
